/**
 * Local checks for a school material chosen on the device (Slices 2–3). A
 * chosen file is previewed in this browser; it leaves the device only when the
 * learner sends it for reading (Slice 3), and the server repeats these checks
 * (`supabase/functions/extract-material/file-check.ts`). Accepted formats
 * follow the proposed P0 starting point (image or PDF, Trust Reference §7.4);
 * the 10 MB limit is kept from Slice 2 and is not yet an approved upload
 * limit, which is still an open PO decision.
 */

export type MaterialKind = 'image' | 'pdf'

export const MAX_FILE_BYTES = 10 * 1024 * 1024

export const IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const

export const PDF_TYPE = 'application/pdf'

export const ACCEPT: Readonly<Record<MaterialKind, string>> = {
  image: IMAGE_TYPES.join(','),
  pdf: PDF_TYPE,
}

export type FileError = 'type' | 'empty' | 'too_large' | 'corrupted'

export type FileCheck = { ok: true; kind: MaterialKind } | { ok: false; error: FileError }

export const FILE_ERROR_TEXT: Readonly<Record<FileError, string>> = {
  type: 'Tệp này không phải ảnh (JPG, PNG, WEBP, HEIC) hoặc PDF. Con chọn tệp khác nhé.',
  empty: 'Tệp này trống. Con chọn tệp khác nhé.',
  too_large: 'Tệp lớn quá (tối đa 10 MB). Con chọn tệp nhỏ hơn nhé.',
  corrupted:
    'Tệp này bị hỏng hoặc không đúng là ảnh/PDF nên không mở được. Con chọn tệp khác nhé.',
}

function kindOf(file: File): MaterialKind | null {
  const type = file.type.toLowerCase()
  if ((IMAGE_TYPES as readonly string[]).includes(type)) return 'image'
  if (type === PDF_TYPE) return 'pdf'
  // Some phones report HEIC photos without a MIME type.
  if (type === '' && /\.(heic|heif)$/i.test(file.name)) return 'image'
  return null
}

/** `expected`: the picker the child used; a PDF chosen as a photo is refused. */
export function checkFile(file: File, expected: MaterialKind): FileCheck {
  const kind = kindOf(file)
  if (kind !== expected) return { ok: false, error: 'type' }
  if (file.size === 0) return { ok: false, error: 'empty' }
  if (file.size > MAX_FILE_BYTES) return { ok: false, error: 'too_large' }
  return { ok: true, kind }
}

export function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

type SniffedType =
  'image/jpeg' | 'image/png' | 'image/webp' | 'image/heic' | 'application/pdf'

const ascii = (bytes: Uint8Array, from: number, length: number): string =>
  String.fromCharCode(...bytes.subarray(from, from + length))

/** The file's real type from its first bytes (same rules as the server). */
export function sniffType(bytes: Uint8Array): SniffedType | null {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg'
  if (bytes[0] === 0x89 && ascii(bytes, 1, 3) === 'PNG') return 'image/png'
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return 'image/webp'
  if (
    ascii(bytes, 4, 4) === 'ftyp' &&
    ['heic', 'heix', 'heim', 'heis', 'hevc', 'mif1', 'msf1'].includes(ascii(bytes, 8, 4))
  )
    return 'image/heic'
  if (ascii(bytes, 0, 5) === '%PDF-') return 'application/pdf'
  return null
}

/**
 * Whether the content really is the kind of file chosen. Catches damaged,
 * truncated or renamed files before they are previewed or sent.
 */
export async function checkContent(file: File, kind: MaterialKind): Promise<boolean> {
  try {
    const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer())
    const type = sniffType(bytes)
    return kind === 'pdf'
      ? type === 'application/pdf'
      : type !== null && type !== PDF_TYPE
  } catch {
    return false
  }
}

/**
 * The file as it is sent for reading, with the type its content proved. Some
 * phones report HEIC photos without a MIME type; the server needs one.
 */
export async function forUpload(file: File): Promise<File> {
  const type = sniffType(new Uint8Array(await file.slice(0, 16).arrayBuffer()))
  if (type === null || type === file.type) return file
  return new File([file], file.name, { type })
}
